import { AlertTriangle } from 'lucide-react'
import { useEffect } from 'react'
import {
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
  type FieldErrors,
} from 'react-hook-form'

import { type UserFormInput } from '@/lib/validation/user-schemas'

interface ClientRolesSectionProps {
  register: UseFormRegister<UserFormInput>
  setValue: UseFormSetValue<UserFormInput>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
}

export function ClientRolesSection({
  register: _register,
  setValue,
  watch,
  errors: _errors,
}: ClientRolesSectionProps) {
  const clientRoles = watch('clientRoles') ?? {}
  const realmRoles = watch('realmRoles')

  const requiredRoles = [
    'manage-users',
    'view-users',
    'query-users',
    'query-groups',
    'view-clients',
    'view-realm',
  ]
  const realmManagementRoles = clientRoles['realm-management'] ?? []
  const hasAllRequiredRoles = requiredRoles.every((role) => realmManagementRoles.includes(role))

  const isRootOperator = realmRoles.cs_sys_op === true

  useEffect(() => {
    if (!isRootOperator && hasAllRequiredRoles) {
      setValue('clientRoles', {})
    }
  }, [isRootOperator, hasAllRequiredRoles, setValue])

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${!isRootOperator ? 'opacity-50' : ''}`}
    >
      <div className="p-6 space-y-6">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            User Management
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Display user management permissions assigned in Keycloak
          </p>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-2">Manual assignment required</p>
              <p className="text-blue-700 dark:text-blue-300 mb-2">
                User management permissions cannot be assigned through the API. A Keycloak
                Administrator must manually assign these realm-management client roles in Keycloak:
              </p>
              <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 space-y-1 mb-2">
                <li>manage-users</li>
                <li>view-users</li>
                <li>query-users</li>
                <li>query-groups</li>
                <li>view-clients</li>
                <li>view-realm</li>
              </ul>
              <p className="text-blue-700 dark:text-blue-300">
                <strong>Requirements:</strong> The user must be a Root Operator (cs_sys_op realm
                role) and have all six realm-management client roles assigned by a Keycloak
                Administrator. This grants system-wide access to create and edit users across all
                systems. Assign sparingly to trusted administrators only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
